import { Column, ColumnGroup } from "@ag-grid-community/core";
import { useMemo } from "react";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import { getDataTableAgHeaderComponentParams } from "shared/components/table/data/DataTableUtils";
import {
  genericColumnFromAgColGroupDef,
  getRootGenericTableColumnFromAgColumnGroup,
  isGenericTableColumn,
} from "shared/components/table/data/internal/DataTableInternalUtils";

export const buildDataTableColumnContext = (
  tableContext: DataTableContext,
  agColumn: Column,
): DataTableColumnContext => {
  if (!isGenericTableColumn(agColumn)) {
    // this usually means some code needs to filter for COLUMN_ID_ACTIONS and COLUMN_ID_AUTO_GROUP
    throw new Error(
      `contextForAgColumn called for non-GenericTableColumn ${agColumn.getColId()}`,
    );
  }
  const { column, parentColumns } =
    getDataTableAgHeaderComponentParams(agColumn);
  return {
    agColumnOrGroup: agColumn,
    column,
    columnId: agColumn.getId(),
    rootColumn: parentColumns?.at(0),
    inferredData: tableContext.columnInferredData.get(agColumn.getColId()),
  };
};

export const buildDataTableColumnGroupContext = (
  tableContext: DataTableContext,
  agColumnGroup: ColumnGroup,
): DataTableColumnContext | undefined => {
  const column = genericColumnFromAgColGroupDef(agColumnGroup.getColGroupDef());
  if (!column) {
    return undefined;
  }
  return {
    agColumnOrGroup: agColumnGroup,
    column,
    columnId: agColumnGroup.getGroupId(),
    rootColumn: getRootGenericTableColumnFromAgColumnGroup(agColumnGroup),
    inferredData: tableContext.columnInferredData.get(
      agColumnGroup.getGroupId(),
    ),
  };
};

export const useBuildDataTableColumnContext = (
  tableContext: DataTableContext,
  agColumn: Column,
): DataTableColumnContext =>
  useMemo(
    () => buildDataTableColumnContext(tableContext, agColumn),
    [tableContext, agColumn],
  );

export const useBuildDataTableColumnGroupContext = (
  tableContext: DataTableContext,
  agColumnGroup: ColumnGroup,
): DataTableColumnContext | undefined =>
  useMemo(
    () => buildDataTableColumnGroupContext(tableContext, agColumnGroup),
    [tableContext, agColumnGroup],
  );
